import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  List,
  PageHeader,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { get } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useQuery } from 'react-apollo';
import { Link } from 'react-router-dom';
import { ADMIN_TREASURY } from './apollo_queries';
import { MovementModal, bankLabel } from '../components/treasury/MovementModal';
import { TreasuryWithdrawModal } from '../components/treasury/TreasuryWithdrawModal';
import { fmtMoney } from '../components/investment-rules/shared';
import { TableMainLabel, TableSubLabel } from '../components/styled';

// Treasury — the admin's HOME (2026-09-17). Replaces the legacy "Manage Sila" page: Constitution's own
// Sila wallet (the treasury handle, under which every note's wallet hangs and from which paybacks are
// paid), the banks linked to it, and the money moved in and out. The balance is LIVE from Sila on every
// load; the movements are ours and refresh their status from Sila on every load too. Rewards, the admin's
// own KYC forms and the Plaid link of the legacy page are not here on purpose.

const PAGE_SIZE = 20;

const STATUS_COLOR = {
  pending: 'gold',
  success: 'green',
  failed: 'red',
  review: 'orange',
};

const shortId = (id) => (id ? `${id.slice(0, 8)}…` : '—');

// The operator reads names, never ids (TL, 2026-09-18): the note by its name, the bank the way the
// picker shows it, and everything technical (Sila id, note id, admin id) in the row's fold.
const columns = (banks) => [
  {
    title: 'When',
    dataIndex: 'createdAt',
    width: 170,
    render: (at, row) => (
      <>
        <TableMainLabel>{moment(at).format('YYYY-MM-DD HH:mm')}</TableMainLabel>
        {row.settledAt && (
          <TableSubLabel>
            settled {moment(row.settledAt).format('YYYY-MM-DD HH:mm')}
          </TableSubLabel>
        )}
      </>
    ),
  },
  {
    title: 'Movement',
    dataIndex: 'direction',
    width: 130,
    render: (direction) =>
      direction === 'deposit' ? (
        <Tag icon={<ArrowDownOutlined />} color="blue">
          Fund
        </Tag>
      ) : (
        <Tag icon={<ArrowUpOutlined />} color="volcano">
          Withdraw
        </Tag>
      ),
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    align: 'right',
    width: 130,
    render: (amount) => <TableMainLabel>{fmtMoney(amount)}</TableMainLabel>,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    width: 120,
    render: (status, row) => (
      <Tooltip title={row.failureReason || null}>
        <Tag color={STATUS_COLOR[status] || 'default'}>{status}</Tag>
      </Tooltip>
    ),
  },
  {
    // Set only on a withdrawal from a NOTE's wallet; the house's own moves read "Treasury wallet".
    title: 'From / to',
    dataIndex: 'investmentId',
    width: 220,
    ellipsis: true,
    render: (id, row) =>
      id ? (
        <Link to={`/properties-and-investments/${id}`}>
          {row.noteName || 'Note'}
        </Link>
      ) : (
        <TableSubLabel>Treasury wallet</TableSubLabel>
      ),
  },
  {
    title: 'Bank',
    dataIndex: 'bankAccountName',
    width: 200,
    render: (name) => {
      const bank = (banks || []).find((b) => b.accountName === name);
      return <TableSubLabel>{bank ? bankLabel(bank) : name}</TableSubLabel>;
    },
  },
  {
    title: 'Reason',
    dataIndex: 'reason',
    ellipsis: true,
  },
];

// The fold under a row: what support needs and an operator does not (click the row to open it).
function MovementDetail({ row }) {
  const line = (label, value) => (
    <div style={{ marginBottom: 4 }}>
      <Typography.Text type="secondary">{label}: </Typography.Text>
      {value}
    </div>
  );
  return (
    <div style={{ padding: '4px 8px' }}>
      {line('Reason', row.reason)}
      {row.failureReason && line('Failure', row.failureReason)}
      {line(
        'Sila transaction',
        row.transactionId ? (
          <Typography.Text code copyable>
            {row.transactionId}
          </Typography.Text>
        ) : (
          <Tag color="red">no id — cannot be reconciled automatically</Tag>
        )
      )}
      {row.investmentId &&
        line(
          'Note id',
          <Typography.Text code copyable>
            {row.investmentId}
          </Typography.Text>
        )}
      {line(
        'Requested by',
        <Typography.Text code>{row.adminId}</Typography.Text>
      )}
      {line('Movement id', <Typography.Text code>{row.id}</Typography.Text>)}
    </div>
  );
}

function BalanceCard({ wallet, loading }) {
  return (
    <Card
      title="Constitution balance"
      loading={loading}
      style={{ height: '100%' }}
    >
      {wallet ? (
        <Row gutter={24}>
          <Col span={12}>
            <Statistic
              title="Available"
              value={
                wallet.availableBalance == null ? '—' : wallet.availableBalance
              }
              precision={2}
              prefix="$"
              valueStyle={{ fontSize: 32, fontWeight: 600 }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Balance"
              value={wallet.balance == null ? '—' : wallet.balance}
              precision={2}
              prefix="$"
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Pending"
              value={
                wallet.pendingBalance == null ? '—' : wallet.pendingBalance
              }
              precision={2}
              prefix="$"
            />
          </Col>
        </Row>
      ) : (
        <Alert
          type="warning"
          showIcon
          message="Sila could not be reached"
          description="The balance is read live from Sila and is unavailable right now. The movements below are ours and still show."
        />
      )}
    </Card>
  );
}

function BanksCard({ banks, loading }) {
  return (
    <Card title="Linked banks" loading={loading} style={{ height: '100%' }}>
      {banks == null ? (
        <Alert type="warning" showIcon message="Sila could not be reached" />
      ) : banks.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No bank is linked to the treasury in Sila. Fund and Withdraw stay disabled until one is."
        />
      ) : (
        <List
          size="small"
          dataSource={banks}
          renderItem={(bank) => (
            <List.Item>
              <List.Item.Meta
                title={bankLabel(bank)}
                description={[bank.accountType, bank.accountName]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
}

export function Treasury() {
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null); // 'deposit' | 'withdraw' | null
  // The Sila identifiers are support data, not what an operator reads: folded by default.
  const [technical, setTechnical] = useState(false);
  // network-only: the balance is a live read and a cached copy would show money that already moved.
  const { data, loading, error, refetch } = useQuery(ADMIN_TREASURY, {
    variables: { page, count: PAGE_SIZE },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const treasury = get(data, 'adminTreasury');
  const wallet = get(treasury, 'wallet', null);
  const banks = get(treasury, 'banks', null);
  const movements = get(treasury, 'movements', { items: [], total: 0 });
  const canMove = Array.isArray(banks) && banks.length > 0 && !!wallet;

  return (
    <>
      <PageHeader
        title="Treasury"
        subTitle="Constitution's own Sila wallet: balance, linked banks and the money moved in and out"
        style={{ padding: '0 0 16px' }}
        extra={[
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>,
          <Tooltip
            key="fund"
            title={
              canMove ? '' : 'Needs the wallet and at least one linked bank'
            }
          >
            <Button
              type="primary"
              icon={<ArrowDownOutlined />}
              disabled={!canMove}
              onClick={() => setModal('deposit')}
            >
              Fund
            </Button>
          </Tooltip>,
          <Tooltip
            key="withdraw"
            title={
              canMove ? '' : 'Needs the wallet and at least one linked bank'
            }
          >
            <Button
              danger
              icon={<ArrowUpOutlined />}
              disabled={!canMove}
              onClick={() => setModal('withdraw')}
            >
              Withdraw
            </Button>
          </Tooltip>,
        ]}
      >
        {treasury && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, fontSize: 12 }}
              onClick={() => setTechnical((open) => !open)}
            >
              {technical ? 'Hide technical details' : 'Technical details'}
            </Button>
            {technical && (
              <>
                {' '}
                · Sila handle{' '}
                <Typography.Text code copyable>
                  {treasury.handle}
                </Typography.Text>
                {wallet && wallet.walletId && (
                  <>
                    {' '}
                    · wallet{' '}
                    <Typography.Text code copyable={{ text: wallet.walletId }}>
                      {shortId(wallet.walletId)}
                    </Typography.Text>
                  </>
                )}
              </>
            )}
          </Typography.Text>
        )}
      </PageHeader>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="The treasury could not be read"
          description={get(error, 'graphQLErrors[0].message') || error.message}
        />
      )}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={14}>
          <BalanceCard wallet={wallet} loading={loading && !treasury} />
        </Col>
        <Col xs={24} lg={10}>
          <BanksCard banks={banks} loading={loading && !treasury} />
        </Col>
      </Row>

      <Card
        title={
          <Space>
            Movements
            <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
              {movements.total} total
            </Typography.Text>
          </Space>
        }
        bodyStyle={{ padding: 0 }}
      >
        <Table
          rowKey="id"
          columns={columns(banks)}
          dataSource={movements.items}
          expandable={{
            expandedRowRender: (row) => <MovementDetail row={row} />,
            expandRowByClick: true,
          }}
          loading={loading}
          size="middle"
          locale={{
            emptyText: 'No money has been moved in or out of the treasury yet',
          }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: movements.total,
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      </Card>

      {modal === 'deposit' && (
        <MovementModal
          direction="deposit"
          banks={banks || []}
          onClose={() => setModal(null)}
          onDone={refetch}
        />
      )}
      {modal === 'withdraw' && (
        <TreasuryWithdrawModal
          banks={banks || []}
          treasuryAvailable={wallet ? wallet.availableBalance : null}
          onClose={() => setModal(null)}
          onDone={refetch}
        />
      )}
    </>
  );
}
